import React, { useEffect, useState } from "react"
import { Grid, Box } from "@material-ui/core"
import { Helmet } from "react-helmet"
import { AudioProject } from "Project";
import { useParams } from "react-router-dom"
import { SongModel } from "models/SongModel"
import styles from "./songdebugpage.module.scss"
import PlayerControls from "components/PlayerControls/PlayerControls"
import MouthBackground from "components/MouthBackground/MouthBackground"
import JSONPretty from "react-json-pretty"
import JSZip from "jszip";
import { getAllSongStems, getAudioProject, getSong, getSongAlbumArt, getSongPlaybackUrl } from "firestoreutils/firestoresongs";
import { getUserProfilePic } from "firestoreutils/firestoreusers";

interface Props {
  songId: string
}

interface StemFile {
  url: string
  filename: string
}

const SongDebugPage = () => {
  const { songId } = useParams<Props>()
  const [song, setSong] = useState<SongModel>()
  const [project, setProject] = useState<any>()
  const [profilePicUrl, setProfilePicUrl] = useState<string>()
  const [albumArt, setAlbumArt] = useState<string>()
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false)
  const [songPlayUrl, setSongPlayUrl] = useState<string>("")
  const [stemFiles, setStemFiles] = useState<StemFile[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    Promise.all([getSong(songId), getAudioProject(songId)]).then(([song, audioProject]) => {
      if (!song) {
        console.error("No such song!");
        setError(true);
        return;
      }

      setSong(song);
      return Promise.all([
        getSongAlbumArt(song),
        getSongPlaybackUrl(song.songID),
        getUserProfilePic(song.creatorID),
        getAllSongStems(song.songID, audioProject),
      ]).then(([albumArt, playUrl, creatorProfileUrl, songStemUrlsAndFiles]) => {
        if (!albumArt || !playUrl) {
          setError(true);
          return;
        }
        setAlbumArt(albumArt);
        setSongPlayUrl(playUrl);
        setProfilePicUrl(creatorProfileUrl);
        setStemFiles(songStemUrlsAndFiles);
        if (audioProject) {
          setProject(AudioProject.toJSON(audioProject));
        }
      });
    });
  }, [])

  useEffect(() => {
    // @ts-ignore
    document.fonts.ready.then(() => {
      setFontsLoaded(true);
    });
  }, [])

  useEffect(() => {
    if (songPlayUrl && song && albumArt && fontsLoaded) {
      setLoading(false)
    }
  }, [songPlayUrl, song, albumArt, fontsLoaded])

  let bodyContent = <div className={styles.loadingPlaceholder}></div>
  if (error) {
    bodyContent = errorContent()
  } else if (!loading) {
    bodyContent = songMetadata(songPlayUrl, albumArt!, profilePicUrl || "", stemFiles, song!)
  }
  return (
    <>
      <Helmet>
        <title>Mayk Song</title>
      </Helmet>
      <MouthBackground />
      <Grid
        alignItems='center'
        justify='center'
        container
        className={styles.grid}
      >
        <Box className={styles.rootCard}>
          {bodyContent}
        </Box>
        <div className={styles.jsonDataContainer}>
          <JSONPretty data={song}></JSONPretty>
        </div>
        <div className={styles.jsonDataContainer}>
          <JSONPretty data={project}></JSONPretty>
        </div>
      </Grid>
    </>
  )
}

const songMetadata = (
  songPlayUrl: string,
  albumArtUrl: string,
  profilePicUrl: string,
  stemFiles: StemFile[],
  song: SongModel
) => {
  return (
    <>
    {headerContent()}
    <div className={styles.songMetadataContainer}>
      <div className={styles.albumArtWrapper}>
        <img src={albumArtUrl} className={styles.albumArt} />
      </div>
      <Box display='flex' flexDirection='row' margin='0 32px'>
        <div className={styles.songMetadata}>
          <div className={styles.songName}>{song?.title}</div>
          <div className={styles.userName}>{song?.artistName}</div>
        </div>
          <img className={styles.userProfile} src={profilePicUrl} />
      </Box>
      <div className={styles.controlsContainer}>
        <PlayerControls songUrl={songPlayUrl} songTitle={song.title} songId={song.songID}></PlayerControls>
      </div>
      <div className={styles.downloadAppButtonOutline}>
        <a className={styles.downloadStemsButton} onClick={() => downloadAllStems(stemFiles, song.title)}>
          Download all stems
        </a>
      </div>
    </div>
    </>
  )
}

const downloadAllStems = async (files: StemFile[], songTitle: string) => {
  const zip = new JSZip();
  await Promise.all(files.map(async (file) => {
    await fetch(file.url)
      .then(res => res.blob())
      .then(buffer => {
        zip.file(file.filename, buffer);
      });
  }));
  const zipFile = await zip.generateAsync({ type: 'blob' });
  saveAs(zipFile, `${songTitle}.zip`);
};

const errorContent = () => {
  return (
    <Box className={styles.errorContent}>
      {headerContent()}
      <img
        className={styles.errorIcon}
        src={require("../../resources/error.svg")}
      />
      <p className={styles.errorText}>Couldn't find that jam...</p>
    </Box>
  )
}

const headerContent = () => {
  return (
    <Box justifyContent='center' display='flex' alignItems='center'>
        <img src={process.env.PUBLIC_URL + "/logo256.png"} alt='logo' className={styles.maykLogo} />
        <h3 className={styles.maykTitle}>mayk</h3>
    </Box>
  )
}

export default SongDebugPage
